import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title, useGetIdentity, useNotify } from 'react-admin';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControlLabel,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import { adsProxy, expertSlaProxy } from './apiGateway';
import {
    buildEntityTitleMaps,
    EntityTitleMaps,
    TariffActor,
    formatTariffHistoryActorLabel
} from './ExpertTariffsPage';

type CategoryByGenderItem = {
    id: string;
    title: string;
    categories: CategoryItem[];
};

type CategoryItem = {
    id: string;
    title: string;
    subcategories: SubcategoryItem[];
};

type SubcategoryItem = {
    id: string;
    title: string;
};

type SlaInputMap = Record<string, string>;
type SlaNumberMap = Record<string, number>;

type PdfJpegPage = {
    jpegBytes: Uint8Array;
    widthPx: number;
    heightPx: number;
};

export type SlaHistoryItem = {
    id: string;
    createdAt: string | null;
    updatedBy: TariffActor;
    slaEnabled: boolean;
    penaltyPercent: number;
    categoryCount: number;
    subcategoryCount: number;
};

export type SlaHistoryRevision = {
    id: string;
    createdAt: string | null;
    updatedAt: string | null;
    updatedBy: TariffActor;
    defaultSlaHours: number;
    slaEnabled: boolean;
    penaltyPercent: number;
    categorySlaHours: SlaNumberMap;
    subcategorySlaHours: SlaNumberMap;
};

function normalizeEntityKey(value: string | null | undefined) {
    return typeof value === 'string' ? value.trim() : '';
}

function roundTo2(value: number) {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function parsePenaltyPercentInput(value: string) {
    const normalized = String(value ?? '').trim();
    if (!normalized) {
        return null;
    }
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0 || parsed > 100) {
        return null;
    }
    return parsed;
}

function convertSlaNumbersToInputs(source: SlaNumberMap | null | undefined): SlaInputMap {
    if (!source || typeof source !== 'object') {
        return {};
    }

    const input: SlaInputMap = {};
    Object.entries(source).forEach(([rawKey, rawValue]) => {
        const key = normalizeEntityKey(rawKey);
        const numberValue = Number(rawValue);
        if (!key || !Number.isFinite(numberValue) || numberValue < 0) {
            return;
        }
        input[key] = String(roundTo2(numberValue));
    });
    return input;
}

function parseSlaInputs(source: SlaInputMap, entityLabel: string): SlaNumberMap {
    const parsed: SlaNumberMap = {};

    Object.entries(source).forEach(([rawKey, rawInputValue]) => {
        const key = normalizeEntityKey(rawKey);
        if (!key) {
            return;
        }

        const normalizedInputValue = String(rawInputValue ?? '').trim();
        if (!normalizedInputValue) {
            return;
        }

        const parsedValue = Number(normalizedInputValue.replace(',', '.'));
        if (!Number.isFinite(parsedValue) || parsedValue < 0) {
            throw new Error(`Некорректное значение SLA в ${entityLabel} (${key})`);
        }
        parsed[key] = roundTo2(parsedValue);
    });

    return parsed;
}

function getParsedInputValue(inputMap: SlaInputMap, key: string): number | null {
    const rawValue = inputMap[key];
    const cleanedValue = typeof rawValue === 'string' ? rawValue.trim() : '';
    if (!cleanedValue) {
        return null;
    }
    const parsedValue = Number(cleanedValue.replace(',', '.'));
    if (!Number.isFinite(parsedValue) || parsedValue < 0) {
        return null;
    }
    return roundTo2(parsedValue);
}

function formatDateTime(value: string | null) {
    if (!value) {
        return 'не обновлялось';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return 'неизвестно';
    }
    return date.toLocaleString('ru-RU');
}

function sanitizeCanvasText(value: string) {
    return String(value || '').replace(/\r?\n/g, ' ').trim();
}

function resolveEntityTitle(entityKey: string, map?: Record<string, string>) {
    const normalizedKey = normalizeEntityKey(entityKey);
    if (!normalizedKey || !map) {
        return normalizedKey || entityKey;
    }
    return map[normalizedKey] || map[normalizedKey.toLowerCase()] || normalizedKey;
}

function trimCanvasTextToWidth(ctx: CanvasRenderingContext2D, value: string, maxWidth: number) {
    const normalized = sanitizeCanvasText(value);
    if (!normalized) {
        return '-';
    }
    if (ctx.measureText(normalized).width <= maxWidth) {
        return normalized;
    }

    let trimmed = normalized;
    while (trimmed.length > 1 && ctx.measureText(`${trimmed}...`).width > maxWidth) {
        trimmed = trimmed.slice(0, -1);
    }
    return `${trimmed}...`;
}

function jpegDataUrlToBytes(dataUrl: string) {
    const base64 = String(dataUrl || '').split(',')[1] || '';
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
}

function concatUint8Arrays(chunks: Uint8Array[]) {
    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;
    chunks.forEach((chunk) => {
        result.set(chunk, offset);
        offset += chunk.length;
    });
    return result;
}

function buildPdfFromJpegPages(pages: PdfJpegPage[]) {
    const safePages = pages.length ? pages : [{
        jpegBytes: new Uint8Array(),
        widthPx: 1,
        heightPx: 1
    }];
    const encoder = new TextEncoder();
    const pageCount = safePages.length;
    const maxObjId = 2 + pageCount * 3;
    const chunks: Uint8Array[] = [];
    const offsets = new Array(maxObjId + 1).fill(0);
    let currentOffset = 0;

    const pushString = (value: string) => {
        const bytes = encoder.encode(value);
        chunks.push(bytes);
        currentOffset += bytes.length;
    };

    const pushBytes = (value: Uint8Array) => {
        chunks.push(value);
        currentOffset += value.length;
    };

    const pageWidthPt = 842;
    const pageHeightPt = 595;

    pushString('%PDF-1.4\n');

    offsets[1] = currentOffset;
    pushString('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

    const pageObjectIds: number[] = [];
    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
        pageObjectIds.push(5 + pageIndex * 3);
    }
    offsets[2] = currentOffset;
    pushString(`2 0 obj\n<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageCount} >>\nendobj\n`);

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
        const imageObjId = 3 + pageIndex * 3;
        const contentObjId = 4 + pageIndex * 3;
        const pageObjId = 5 + pageIndex * 3;
        const page = safePages[pageIndex];

        offsets[imageObjId] = currentOffset;
        pushString(`${imageObjId} 0 obj\n`);
        pushString(`<< /Type /XObject /Subtype /Image /Width ${Math.max(1, Math.round(page.widthPx))} /Height ${Math.max(1, Math.round(page.heightPx))} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.jpegBytes.length} >>\n`);
        pushString('stream\n');
        pushBytes(page.jpegBytes);
        pushString('\nendstream\nendobj\n');

        const contentStream = `q\n${pageWidthPt} 0 0 ${pageHeightPt} 0 0 cm\n/Im0 Do\nQ\n`;
        offsets[contentObjId] = currentOffset;
        pushString(`${contentObjId} 0 obj\n<< /Length ${encoder.encode(contentStream).length} >>\nstream\n${contentStream}endstream\nendobj\n`);

        offsets[pageObjId] = currentOffset;
        pushString(`${pageObjId} 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidthPt} ${pageHeightPt}] /Resources << /XObject << /Im0 ${imageObjId} 0 R >> >> /Contents ${contentObjId} 0 R >>\nendobj\n`);
    }

    const xrefOffset = currentOffset;
    pushString(`xref\n0 ${maxObjId + 1}\n`);
    pushString('0000000000 65535 f \n');
    for (let objectId = 1; objectId <= maxObjId; objectId += 1) {
        pushString(`${String(offsets[objectId]).padStart(10, '0')} 00000 n \n`);
    }
    pushString(`trailer\n<< /Size ${maxObjId + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

    return new Blob([concatUint8Arrays(chunks)], { type: 'application/pdf' });
}

export function buildSlaHistoryRevisionPdfBlob(revision: SlaHistoryRevision, entityTitles?: EntityTitleMaps) {
    const categoryEntries = Object.entries(revision.categorySlaHours || {}).sort(([left], [right]) => left.localeCompare(right));
    const subcategoryEntries = Object.entries(revision.subcategorySlaHours || {}).sort(([left], [right]) => left.localeCompare(right));

    const sections = [
        {
            title: `SLA категорий (${categoryEntries.length})`,
            rows: categoryEntries.map(([key, value]) => ({
                key: resolveEntityTitle(key, entityTitles?.categoryTitlesById),
                value: String(roundTo2(Number(value)))
            }))
        },
        {
            title: `SLA подкатегорий (${subcategoryEntries.length})`,
            rows: subcategoryEntries.map(([key, value]) => ({
                key: resolveEntityTitle(key, entityTitles?.subcategoryTitlesById),
                value: String(roundTo2(Number(value)))
            }))
        }
    ];

    const canvasWidth = 1684;
    const canvasHeight = 1191;
    const marginLeft = 40;
    const marginRight = 40;
    const marginTop = 40;
    const marginBottom = 40;
    const rowHeight = 28;
    const tableWidth = canvasWidth - marginLeft - marginRight;
    const firstColWidth = 1200;
    const secondColWidth = tableWidth - firstColWidth;
    const pageCanvases: HTMLCanvasElement[] = [];

    const createPage = () => {
        const canvas = document.createElement('canvas');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Не удалось инициализировать canvas для PDF');
        }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
        ctx.strokeStyle = '#2f2f2f';
        ctx.fillStyle = '#111111';
        ctx.textBaseline = 'middle';
        pageCanvases.push(canvas);
        return ctx;
    };

    let ctx = createPage();
    let currentY = marginTop;

    const ensureSpace = (requiredHeight: number) => {
        if (currentY + requiredHeight <= canvasHeight - marginBottom) {
            return false;
        }
        ctx = createPage();
        currentY = marginTop;
        return true;
    };

    const drawTextCell = (text: string, x: number, y: number, width: number, font: string, color = '#111111') => {
        ctx.font = font;
        ctx.fillStyle = color;
        const value = trimCanvasTextToWidth(ctx, text, Math.max(0, width - 8));
        ctx.fillText(value, x + 4, y + rowHeight / 2);
    };

    const drawTableHeader = () => {
        ctx.fillStyle = '#f1f4f8';
        ctx.fillRect(marginLeft, currentY, tableWidth, rowHeight);
        ctx.strokeStyle = '#2f2f2f';
        ctx.strokeRect(marginLeft, currentY, tableWidth, rowHeight);

        let x = marginLeft;
        ctx.strokeRect(x, currentY, firstColWidth, rowHeight);
        drawTextCell('Категория / подкатегория', x, currentY, firstColWidth, '600 14px Arial', '#1d1d1d');
        x += firstColWidth;

        ctx.strokeRect(x, currentY, secondColWidth, rowHeight);
        drawTextCell('SLA (часы)', x, currentY, secondColWidth, '600 14px Arial', '#1d1d1d');
        currentY += rowHeight;
    };

    const drawDataRow = (entityName: string, slaHours: string) => {
        ctx.strokeStyle = '#2f2f2f';
        ctx.strokeRect(marginLeft, currentY, tableWidth, rowHeight);

        let x = marginLeft;
        ctx.strokeRect(x, currentY, firstColWidth, rowHeight);
        drawTextCell(entityName, x, currentY, firstColWidth, '13px Arial');
        x += firstColWidth;

        ctx.strokeRect(x, currentY, secondColWidth, rowHeight);
        drawTextCell(slaHours, x, currentY, secondColWidth, '13px Arial');
        currentY += rowHeight;
    };

    const metadataLines = [
        'Редакция SLA экспертов',
        `ID редакции: ${sanitizeCanvasText(revision.id)}`,
        `Дата редакции: ${sanitizeCanvasText(revision.createdAt || '-')}`,
        `Редактор: ${sanitizeCanvasText(formatTariffHistoryActorLabel(revision.updatedBy))}`,
        `SLA включен: ${revision.slaEnabled !== false ? 'Да' : 'Нет'}`,
        `Штраф за нарушение SLA: ${Math.max(0, Math.min(100, Math.round(Number(revision.penaltyPercent ?? 10))))}%`,
        `SLA по умолчанию (часы): ${roundTo2(Number(revision.defaultSlaHours || 4))}`,
        `Категорий: ${categoryEntries.length}, подкатегорий: ${subcategoryEntries.length}`
    ];

    metadataLines.forEach((line, index) => {
        ensureSpace(24);
        ctx.font = index === 0 ? '700 24px Arial' : '16px Arial';
        ctx.fillStyle = '#111111';
        const trimmed = trimCanvasTextToWidth(ctx, line, tableWidth);
        ctx.fillText(trimmed, marginLeft, currentY + 12);
        currentY += index === 0 ? 34 : 24;
    });

    currentY += 8;

    sections.forEach((section) => {
        ensureSpace(52);
        ctx.font = '700 18px Arial';
        ctx.fillStyle = '#111111';
        const title = sanitizeCanvasText(section.title);
        ctx.fillText(title, marginLeft, currentY + 12);
        currentY += 24;
        drawTableHeader();

        if (!section.rows.length) {
            drawDataRow('Нет данных', '-');
            currentY += 12;
            return;
        }

        section.rows.forEach((row) => {
            if (ensureSpace(rowHeight)) {
                ctx.font = '700 17px Arial';
                ctx.fillStyle = '#111111';
                ctx.fillText(`${title} (продолжение)`, marginLeft, currentY + 10);
                currentY += 22;
                drawTableHeader();
            }
            drawDataRow(sanitizeCanvasText(row.key), sanitizeCanvasText(row.value));
        });

        currentY += 12;
    });

    const jpegPages: PdfJpegPage[] = pageCanvases.map((canvas) => ({
        jpegBytes: jpegDataUrlToBytes(canvas.toDataURL('image/jpeg', 0.92)),
        widthPx: canvas.width,
        heightPx: canvas.height
    }));

    return buildPdfFromJpegPages(jpegPages);
}

export const ExpertSlaPage = () => {
    const notify = useNotify();
    const { identity, isPending } = useGetIdentity();
    const currentUser = (identity ?? null) as { role?: string } | null;
    const isManagerAdmin = currentUser?.role === 'manager-admin';

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [categoriesByGender, setCategoriesByGender] = useState<CategoryByGenderItem[]>([]);
    const [defaultSlaHours, setDefaultSlaHours] = useState(4);
    const [slaEnabled, setSlaEnabled] = useState(true);
    const [penaltyPercentInput, setPenaltyPercentInput] = useState('10');
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [updatedByLabel, setUpdatedByLabel] = useState<string>('');
    const [categorySlaInput, setCategorySlaInput] = useState<SlaInputMap>({});
    const [subcategorySlaInput, setSubcategorySlaInput] = useState<SlaInputMap>({});
    const [history, setHistory] = useState<SlaHistoryItem[]>([]);
    const [expandedGenderPanel, setExpandedGenderPanel] = useState<string | false>(false);
    const [expandedCategoryPanelByGender, setExpandedCategoryPanelByGender] = useState<Record<string, string | false>>({});
    const [downloadingRevisionId, setDownloadingRevisionId] = useState<string | null>(null);

    const loadData = useCallback(async () => {
        if (!isManagerAdmin) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const [slaResponse, adConfigResponse, historyResponse] = await Promise.all([
                expertSlaProxy.getConfig(),
                adsProxy.getAdConfig(),
                expertSlaProxy.getHistory(100)
            ]);

            const adConfigBody = (adConfigResponse?.body ?? adConfigResponse) as {
                categoriesByGender?: CategoryByGenderItem[];
            };
            const resolvedCategoriesByGender = Array.isArray(adConfigBody?.categoriesByGender)
                ? adConfigBody.categoriesByGender
                : [];
            const updatedBy = slaResponse?.updatedBy || null;
            const updatedByLabelValue = [
                normalizeEntityKey(updatedBy?.fullName),
                normalizeEntityKey(updatedBy?.login)
            ].filter(Boolean).join(' / ');

            setCategoriesByGender(resolvedCategoriesByGender);
            setDefaultSlaHours(Number.isFinite(Number(slaResponse?.defaultSlaHours))
                ? roundTo2(Number(slaResponse.defaultSlaHours))
                : 4);
            setSlaEnabled(slaResponse?.slaEnabled !== false);
            setPenaltyPercentInput(String(parsePenaltyPercentInput(String(slaResponse?.penaltyPercent ?? '')) ?? 10));
            setUpdatedAt(typeof slaResponse?.updatedAt === 'string' ? slaResponse.updatedAt : null);
            setUpdatedByLabel(updatedByLabelValue);
            setCategorySlaInput(convertSlaNumbersToInputs(slaResponse?.categorySlaHours as SlaNumberMap));
            setSubcategorySlaInput(convertSlaNumbersToInputs(slaResponse?.subcategorySlaHours as SlaNumberMap));
            setHistory(Array.isArray(historyResponse?.data) ? historyResponse.data as SlaHistoryItem[] : []);
            setExpandedGenderPanel(false);
            setExpandedCategoryPanelByGender({});
        } catch (error) {
            console.error('Failed to load expert SLA page data:', error);
            notify('Не удалось загрузить SLA для экспертов', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [isManagerAdmin, notify]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const entityTitleMaps = useMemo(
        () => buildEntityTitleMaps(categoriesByGender),
        [categoriesByGender]
    );

    const handleCategorySlaInputChange = (categoryKey: string, value: string) => {
        setCategorySlaInput((prev) => ({
            ...prev,
            [categoryKey]: value
        }));
    };

    const handleSubcategorySlaInputChange = (subcategoryKey: string, value: string) => {
        setSubcategorySlaInput((prev) => ({
            ...prev,
            [subcategoryKey]: value
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const categorySlaHours = parseSlaInputs(categorySlaInput, 'категории');
            const subcategorySlaHours = parseSlaInputs(subcategorySlaInput, 'подкатегории');
            const penaltyPercent = parsePenaltyPercentInput(penaltyPercentInput);
            if (penaltyPercent === null) {
                throw new Error('Процент штрафа должен быть целым числом от 0 до 100');
            }
            const response = await expertSlaProxy.updateConfig({
                slaEnabled,
                penaltyPercent,
                categorySlaHours,
                subcategorySlaHours
            });
            const historyResponse = await expertSlaProxy.getHistory(100);

            const updatedBy = response?.updatedBy || null;
            const updatedByLabelValue = [
                normalizeEntityKey(updatedBy?.fullName),
                normalizeEntityKey(updatedBy?.login)
            ].filter(Boolean).join(' / ');

            setUpdatedAt(typeof response?.updatedAt === 'string' ? response.updatedAt : null);
            setUpdatedByLabel(updatedByLabelValue);
            setSlaEnabled(response?.slaEnabled !== false);
            setPenaltyPercentInput(String(parsePenaltyPercentInput(String(response?.penaltyPercent ?? '')) ?? penaltyPercent));
            setCategorySlaInput(convertSlaNumbersToInputs(response?.categorySlaHours as SlaNumberMap));
            setSubcategorySlaInput(convertSlaNumbersToInputs(response?.subcategorySlaHours as SlaNumberMap));
            setHistory(Array.isArray(historyResponse?.data) ? historyResponse.data as SlaHistoryItem[] : []);
            notify('SLA сохранен', { type: 'info' });
        } catch (error) {
            const message = error instanceof Error && error.message.trim()
                ? error.message
                : 'Не удалось сохранить SLA';
            notify(message, { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleDownloadRevisionPdf = async (historyItem: SlaHistoryItem) => {
        const previewWindow = window.open('about:blank', '_blank');
        if (!previewWindow) {
            notify('Браузер заблокировал открытие вкладки. Разрешите pop-up.', { type: 'warning' });
            return;
        }

        previewWindow.document.write('<p>Формируем PDF...</p>');
        previewWindow.document.close();
        setDownloadingRevisionId(historyItem.id);
        try {
            const response = await expertSlaProxy.getHistoryRevision(historyItem.id);
            const revision = response?.data as SlaHistoryRevision | undefined;
            if (!revision) {
                throw new Error('Не удалось получить данные ревизии');
            }

            const blob = buildSlaHistoryRevisionPdfBlob(revision, entityTitleMaps);
            const url = URL.createObjectURL(blob);
            previewWindow.location.href = url;
            window.setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 60_000);
        } catch (error) {
            previewWindow.close();
            const message = error instanceof Error && error.message.trim()
                ? error.message
                : 'Не удалось сформировать PDF';
            notify(message, { type: 'error' });
        } finally {
            setDownloadingRevisionId(null);
        }
    };

    const summaryLabel = useMemo(() => {
        const categoriesCount = categoriesByGender.reduce((acc, gender) => {
            const categories = Array.isArray(gender.categories) ? gender.categories : [];
            return acc + categories.length;
        }, 0);
        const subcategoriesCount = categoriesByGender.reduce((acc, gender) => {
            const categories = Array.isArray(gender.categories) ? gender.categories : [];
            return acc + categories.reduce((innerAcc, category) => {
                const subcategories = Array.isArray(category.subcategories) ? category.subcategories : [];
                return innerAcc + subcategories.length;
            }, 0);
        }, 0);
        return `Категорий: ${categoriesCount}, подкатегорий: ${subcategoriesCount}`;
    }, [categoriesByGender]);

    if (isPending || loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Title title="SLA проверки объявлений" />
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (!isManagerAdmin) {
        return (
            <Box sx={{ p: 3 }}>
                <Title title="SLA проверки объявлений" />
                <Alert severity="error">Доступ только для роли manager-admin</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Title title="SLA проверки объявлений" />
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2, gap: 1.5 }}>
                <Box>
                    <Typography variant="h4">SLA проверки объявлений</Typography>
                    <Typography variant="body2" color="text.secondary">{summaryLabel}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Обновлено: {formatDateTime(updatedAt)}{updatedByLabel ? ` (${updatedByLabel})` : ''}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Сохраняю...' : 'Сохранить SLA'}
                </Button>
            </Stack>

            <Alert severity="info" sx={{ mb: 2 }}>
                SLA по умолчанию: {defaultSlaHours} ч. Если SLA подкатегории не заполнен, берется SLA категории. Если SLA категории не заполнен, берется SLA по умолчанию. Значение 0 отключает SLA для категории/подкатегории.
            </Alert>

            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                        <FormControlLabel
                            control={(
                                <Switch
                                    checked={slaEnabled}
                                    onChange={(event) => setSlaEnabled(event.target.checked)}
                                />
                            )}
                            label={slaEnabled ? 'SLA включен' : 'SLA отключен'}
                        />
                        <TextField
                            size="small"
                            label="Штраф за нарушение SLA, %"
                            type="number"
                            value={penaltyPercentInput}
                            onChange={(event) => setPenaltyPercentInput(event.target.value)}
                            inputProps={{ min: 0, max: 100, step: 1 }}
                            sx={{ width: 240 }}
                        />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        При отключенном SLA штраф не применяется. Процент штрафа применяется только к новым решениям.
                    </Typography>
                </CardContent>
            </Card>

            <Accordion sx={{ mb: 2 }} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">История SLA ({history.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {history.length === 0 ? (
                        <Alert severity="info">История пока пустая. Появится после первого сохранения SLA.</Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Редакция от</TableCell>
                                        <TableCell>Редактор</TableCell>
                                        <TableCell>SLA</TableCell>
                                        <TableCell align="right">Штраф, %</TableCell>
                                        <TableCell align="right">Категорий</TableCell>
                                        <TableCell align="right">Подкатегорий</TableCell>
                                        <TableCell align="right">Файл</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {history.map((historyItem, index) => (
                                        <TableRow key={historyItem.id}>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <span>{formatDateTime(historyItem.createdAt)}</span>
                                                    {index === 0 ? (
                                                        <Chip size="small" color="success" variant="outlined" label="Актуальная" />
                                                    ) : null}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{formatTariffHistoryActorLabel(historyItem.updatedBy)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    color={historyItem.slaEnabled !== false ? 'success' : 'default'}
                                                    variant={historyItem.slaEnabled !== false ? 'filled' : 'outlined'}
                                                    label={historyItem.slaEnabled !== false ? 'Включен' : 'Отключен'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">{historyItem.penaltyPercent ?? 10}</TableCell>
                                            <TableCell align="right">{historyItem.categoryCount ?? 0}</TableCell>
                                            <TableCell align="right">{historyItem.subcategoryCount ?? 0}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={downloadingRevisionId === historyItem.id ? <CircularProgress size={14} /> : <DownloadIcon />}
                                                    onClick={() => handleDownloadRevisionPdf(historyItem)}
                                                    disabled={downloadingRevisionId === historyItem.id}
                                                >
                                                    {downloadingRevisionId === historyItem.id ? 'Формирую...' : 'Скачать PDF'}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </AccordionDetails>
            </Accordion>

            {categoriesByGender.length === 0 ? (
                <Alert severity="warning">Не удалось получить категории из adConfig.</Alert>
            ) : (
                <Stack spacing={2}>
                    {categoriesByGender.map((genderItem) => {
                        const genderCategories = Array.isArray(genderItem.categories) ? genderItem.categories : [];
                        const genderPanelKey = genderItem.id || genderItem.title || 'unknown';
                        const isExpanded = expandedGenderPanel === genderPanelKey;
                        const expandedCategoryPanel = expandedCategoryPanelByGender[genderPanelKey] || false;

                        return (
                            <Accordion
                                key={genderPanelKey}
                                expanded={isExpanded}
                                onChange={(_, expanded) => setExpandedGenderPanel(expanded ? genderPanelKey : false)}
                                slotProps={{ transition: { unmountOnExit: true } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">{genderItem.title || genderItem.id || 'Без названия'}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {!isExpanded ? null : genderCategories.length === 0 ? (
                                        <Alert severity="info">Для этого раздела нет категорий.</Alert>
                                    ) : (
                                        <Stack spacing={1.5}>
                                            {genderCategories.map((category, categoryIndex) => {
                                                const categoryKey = normalizeEntityKey(category.id || category.title);
                                                const subcategories = Array.isArray(category.subcategories) ? category.subcategories : [];
                                                const categoryPanelKey = `${genderPanelKey}::${category.id || category.title || categoryIndex}`;
                                                const isCategoryExpanded = expandedCategoryPanel === categoryPanelKey;

                                                return (
                                                    <Accordion
                                                        key={categoryPanelKey}
                                                        expanded={isCategoryExpanded}
                                                        onChange={(_, expanded) => {
                                                            setExpandedCategoryPanelByGender((prev) => ({
                                                                ...prev,
                                                                [genderPanelKey]: expanded ? categoryPanelKey : false
                                                            }));
                                                        }}
                                                        slotProps={{ transition: { unmountOnExit: true } }}
                                                    >
                                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                                    {category.title || category.id || 'Категория без названия'}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Подкатегорий: {subcategories.length}
                                                                </Typography>
                                                            </Stack>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            {!isCategoryExpanded ? null : (
                                                                <Stack spacing={2}>
                                                                    <Card variant="outlined">
                                                                        <CardContent sx={{ p: 0 }}>
                                                                            <TableContainer>
                                                                                <Table size="small">
                                                                                    <TableHead>
                                                                                        <TableRow>
                                                                                            <TableCell sx={{ minWidth: 280 }}>SLA категории (часов)</TableCell>
                                                                                        </TableRow>
                                                                                    </TableHead>
                                                                                    <TableBody>
                                                                                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                                                                            <TableCell>
                                                                                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                                                                                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                                                                                        {category.title || category.id || 'Категория без названия'}
                                                                                                    </Typography>
                                                                                                    <TextField
                                                                                                        size="small"
                                                                                                        type="number"
                                                                                                        value={categorySlaInput[categoryKey] ?? ''}
                                                                                                        onChange={(event) => handleCategorySlaInputChange(categoryKey, event.target.value)}
                                                                                                        placeholder={String(defaultSlaHours)}
                                                                                                        inputProps={{ min: 0, step: 0.5 }}
                                                                                                        sx={{ width: 120 }}
                                                                                                    />
                                                                                                </Stack>
                                                                                            </TableCell>
                                                                                        </TableRow>
                                                                                    </TableBody>
                                                                                </Table>
                                                                            </TableContainer>
                                                                        </CardContent>
                                                                    </Card>

                                                                    {subcategories.length === 0 ? (
                                                                        <Alert severity="info">У категории нет подкатегорий.</Alert>
                                                                    ) : (
                                                                        <Card variant="outlined">
                                                                            <CardContent sx={{ p: 0 }}>
                                                                                <TableContainer>
                                                                                    <Table size="small">
                                                                                        <TableHead>
                                                                                            <TableRow>
                                                                                                <TableCell sx={{ minWidth: 280 }}>SLA подкатегорий (часов)</TableCell>
                                                                                                <TableCell align="right" sx={{ minWidth: 180 }}>Значение</TableCell>
                                                                                            </TableRow>
                                                                                        </TableHead>
                                                                                        <TableBody>
                                                                                            {subcategories.map((subcategory, subcategoryIndex) => {
                                                                                                const subcategoryKey = normalizeEntityKey(subcategory.id || subcategory.title);
                                                                                                const rowKey = `${categoryPanelKey}::sub::${subcategory.id || subcategory.title || subcategoryIndex}`;
                                                                                                const categoryFallback = getParsedInputValue(categorySlaInput, categoryKey) ?? defaultSlaHours;
                                                                                                return (
                                                                                                    <TableRow key={rowKey}>
                                                                                                        <TableCell>
                                                                                                            <Typography variant="body2">
                                                                                                                {subcategory.title || subcategory.id || 'Подкатегория без названия'}
                                                                                                            </Typography>
                                                                                                        </TableCell>
                                                                                                        <TableCell align="right">
                                                                                                            <TextField
                                                                                                                size="small"
                                                                                                                type="number"
                                                                                                                value={subcategorySlaInput[subcategoryKey] ?? ''}
                                                                                                                onChange={(event) => handleSubcategorySlaInputChange(subcategoryKey, event.target.value)}
                                                                                                                placeholder={String(categoryFallback)}
                                                                                                                inputProps={{ min: 0, step: 0.5 }}
                                                                                                                sx={{ width: 120 }}
                                                                                                            />
                                                                                                        </TableCell>
                                                                                                    </TableRow>
                                                                                                );
                                                                                            })}
                                                                                        </TableBody>
                                                                                    </Table>
                                                                                </TableContainer>
                                                                            </CardContent>
                                                                        </Card>
                                                                    )}
                                                                </Stack>
                                                            )}
                                                        </AccordionDetails>
                                                    </Accordion>
                                                );
                                            })}
                                        </Stack>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Stack>
            )}
        </Box>
    );
};
